import { Fragment, type ReactNode } from 'react';

/**
 * Wandelt URLs und E-Mail-Adressen in klickbare Links um — XSS-sicher durch reines
 * Text-Splitting (KEIN dangerouslySetInnerHTML). Die Linkfarbe erbt `currentColor`
 * (+ Unterstreichung), damit Links sowohl in dunklen (outbound) als auch hellen
 * (inbound) Nachrichtenblasen lesbar bleiben.
 *
 * Telefonnummern werden bewusst NICHT verlinkt — zu viele Falsch-Treffer bei
 * normalen Zahlen im Fließtext.
 */
// Längenbegrenzung (1–2048) statt unbegrenztem `+`, damit extrem lange Tokens
// ohne Whitespace (z. B. eingebettete Base64-Blobs) die Suche nicht ausbremsen.
const TOKEN_RE = /(https?:\/\/[^\s]{1,2048}|www\.[^\s]{1,2048}|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;
// Satzzeichen am Token-Ende gehören nicht in den Link (z. B. „…seite.de.").
const TRAILING_RE = /[.,;:!?)\]}'"»>]+$/;

function toHref(token: string): string {
  if (token.includes('@') && !/^https?:\/\//i.test(token)) return `mailto:${token}`;
  if (/^www\./i.test(token)) return `https://${token}`;
  return token;
}

export function Linkified({ text }: { text: string }): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;

  while ((match = TOKEN_RE.exec(text)) !== null) {
    const start = match.index;
    const raw = match[0];
    const trail = raw.match(TRAILING_RE)?.[0] ?? '';
    const token = trail ? raw.slice(0, -trail.length) : raw;

    if (start > last) parts.push(text.slice(last, start));
    parts.push(
      <a
        key={`l${start}`}
        href={toHref(token)}
        target="_blank"
        rel="noreferrer noopener"
        className="break-all underline underline-offset-2 hover:opacity-80"
        onClick={(e) => e.stopPropagation()}
      >
        {token}
      </a>,
    );
    if (trail) parts.push(trail);
    last = start + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));

  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </>
  );
}
